import {
  savedReplyFields,
  supportKinds,
  supportStateWords,
  SUPPORT_LIMITS,
  type AdminSupportSavedReply,
  type AdminSupportStatus,
  type AdminSupportTag,
  type SupportColor,
  type SupportKind,
} from "@teamna/shared";
import { useState, type FormEvent } from "react";
import {
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  Field,
  InlineError,
  PageHeader,
  SelectField,
  SkeletonList,
  TextAreaField,
  showToast,
} from "@teamna/ui/components/index.ts";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import {
  useCreateSavedReply,
  useCreateSupportStatus,
  useCreateSupportTag,
  useDeleteSavedReply,
  useReorderSupportStatuses,
  useSavedReplies,
  useSupportBoard,
  useUpdateSavedReply,
  useUpdateSupportStatus,
  useUpdateSupportTag,
} from "../lib/cc-api.ts";
import { ColorDot, ColorPicker, KIND_WORDS, SupportTabs } from "./support-parts.tsx";

/**
 * Support settings, `/support/settings` (support-system.md, section 6.5):
 *   - the board columns: add, rename, colour, kind, default for its kind, order, archive. The API
 *     keeps the rules (section 4.2): a column with tickets cannot archive or change its kind, and
 *     the default of a kind cannot either; its answer shows as a toast;
 *   - the labels: add, rename, colour, archive;
 *   - the saved replies: title and text, with `{first_name}` and `{business_name}`.
 * A colour change saves at once; a new name or text saves with its button.
 */

const onError = (error: Error) => showToast(copyForError(error).message, { tone: "info" });

/**
 * A text field of a settings row. With no edit of its own, it follows the server value, so a name
 * a teammate changed shows here and Save never writes the old name back (internal review F3.2 of
 * the team tools). An edit in progress stays: it is what this person chose to save.
 */
function useDraft(value: string): [string, (next: string) => void] {
  const [draft, setDraft] = useState(value);
  const [base, setBase] = useState(value);
  if (value !== base) {
    setBase(value);
    if (draft === base) setDraft(value);
  }
  return [draft, setDraft];
}

export function SupportSettingsScreen() {
  const board = useSupportBoard();

  if (!board.data) {
    return (
      <>
        <PageHeader title="Support" />
        <SupportTabs current="settings" />
        {board.isLoading ? (
          <output aria-label="Loading the settings" className="block">
            <SkeletonList rows={3} />
          </output>
        ) : (
          <ErrorState error={board.error} onRetry={() => void board.refetch()} />
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader title="Support" description="The columns of the board, the labels the team puts on tickets, and the saved replies." />
      <SupportTabs current="settings" />
      <div className="grid gap-6 lg:grid-cols-2">
        <ColumnsCard statuses={board.data.statuses} />
        <LabelsCard tags={board.data.tags} />
        <div className="lg:col-span-2">
          <SavedRepliesCard />
        </div>
      </div>
    </>
  );
}

// --- labels ---------------------------------------------------------------------------------

function LabelsCard({ tags }: { tags: AdminSupportTag[] }) {
  const create = useCreateSupportTag();
  const [name, setName] = useState("");
  const [color, setColor] = useState<SupportColor>("blue");
  const [tried, setTried] = useState(false);
  const nameError = tried && !name.trim() ? "Write a name for the label." : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim(), color },
      {
        onSuccess: (tag) => {
          setName("");
          setTried(false);
          showToast(`Label "${tag.name}" added.`);
        },
      },
    );
  }

  return (
    <Card className="flex flex-col gap-4 p-4">
      <div>
        <h2 className="text-[16px] font-semibold text-ink">Labels</h2>
        <p className="text-[13px] text-ink-2">Put labels on a ticket from its page. Only the Teamna team sees them.</p>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-3" noValidate>
        {create.error ? <InlineError error={create.error} /> : null}
        <Field
          label="New label"
          hint="For example: Bug, Billing, Feature request."
          size="compact"
          value={name}
          maxLength={SUPPORT_LIMITS.tagName}
          onChange={(event) => setName(event.target.value)}
          error={nameError}
          dir="auto"
          autoComplete="off"
        />
        <ColorPicker label="Colour of the new label" value={color} onChange={setColor} />
        <div>
          <Button type="submit" size="sm" loading={create.isPending}>
            Add label
          </Button>
        </div>
      </form>
      {tags.length === 0 ? (
        <p className="text-[14px] text-ink-2">No labels yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line" aria-label="Labels">
          {tags.map((tag) => (
            <li key={tag.id} className="py-3">
              <LabelRow tag={tag} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function LabelRow({ tag }: { tag: AdminSupportTag }) {
  const update = useUpdateSupportTag();
  const [name, setName] = useDraft(tag.name);
  const [archiving, setArchiving] = useState(false);
  const renamed = name.trim() !== "" && name.trim() !== tag.name;

  function rename(event: FormEvent) {
    event.preventDefault();
    if (!renamed) return;
    update.mutate({ tagId: tag.id, name: name.trim() }, { onSuccess: () => showToast("Label renamed."), onError });
  }

  return (
    <div className="flex flex-col gap-2">
      <form onSubmit={rename} className="flex items-end gap-2" noValidate>
        <div className="min-w-0 flex-1">
          <Field
            label="Name"
            size="compact"
            value={name}
            maxLength={SUPPORT_LIMITS.tagName}
            onChange={(event) => setName(event.target.value)}
            dir="auto"
            autoComplete="off"
          />
        </div>
        {renamed ? (
          <Button type="submit" size="sm" loading={update.isPending}>
            Save
          </Button>
        ) : null}
        <Button type="button" size="sm" variant="ghost" onClick={() => setArchiving(true)}>
          Archive
        </Button>
      </form>
      <ColorPicker
        label={`Colour of ${tag.name}`}
        value={tag.color}
        disabled={update.isPending}
        onChange={(color) => update.mutate({ tagId: tag.id, color }, { onError })}
      />
      <ConfirmDialog
        open={archiving}
        title={`Archive the label "${tag.name}"?`}
        confirmLabel="Archive"
        tone="danger"
        loading={update.isPending}
        onCancel={() => setArchiving(false)}
        onConfirm={() =>
          update.mutate(
            { tagId: tag.id, archived: true },
            {
              onSuccess: () => {
                setArchiving(false);
                showToast("Label archived.");
              },
              onError,
            },
          )
        }
      >
        The label leaves every ticket and the filters. You cannot undo this. You can add a new label with the same name.
      </ConfirmDialog>
    </div>
  );
}

// --- columns --------------------------------------------------------------------------------

function KindSelect({ label, value, onChange, disabled }: { label: string; value: SupportKind; onChange: (kind: SupportKind) => void; disabled?: boolean }) {
  return (
    <SelectField label={label} size="compact" value={value} disabled={disabled} onChange={(event) => onChange(event.target.value as SupportKind)}>
      {supportKinds.map((kind) => (
        <option key={kind} value={kind}>
          {KIND_WORDS[kind]}. The sender reads: {supportStateWords[kind]}
        </option>
      ))}
    </SelectField>
  );
}

function ColumnsCard({ statuses }: { statuses: AdminSupportStatus[] }) {
  const create = useCreateSupportStatus();
  const reorder = useReorderSupportStatuses();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<SupportKind>("open");
  const [color, setColor] = useState<SupportColor>("slate");
  const [tried, setTried] = useState(false);
  const nameError = tried && !name.trim() ? "Write a name for the column." : undefined;
  const full = statuses.length >= SUPPORT_LIMITS.statuses;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim(), kind, color },
      {
        onSuccess: (status) => {
          setName("");
          setTried(false);
          showToast(`Column "${status.name}" added at the end of the board.`);
        },
      },
    );
  }

  /** Moves one column a place to the left (-1) or the right (+1) of the board. */
  function shift(index: number, by: -1 | 1) {
    const ids = statuses.map((status) => status.id);
    const other = index + by;
    if (other < 0 || other >= ids.length) return;
    [ids[index], ids[other]] = [ids[other]!, ids[index]!];
    reorder.mutate(ids, { onError });
  }

  return (
    <Card className="flex flex-col gap-4 p-4">
      <div>
        <h2 className="text-[16px] font-semibold text-ink">Columns</h2>
        <p className="text-[13px] text-ink-2">
          The order here is the order of the board, left to right. The kind sets the words the sender reads. New, reopened and auto-closed
          tickets go to the default column of their kind.
        </p>
      </div>
      <ol className="flex flex-col divide-y divide-line" aria-label="Columns">
        {statuses.map((status, index) => (
          <li key={status.id} className="py-3">
            <ColumnRow
              status={status}
              first={index === 0}
              last={index === statuses.length - 1}
              moving={reorder.isPending}
              onShift={(by) => shift(index, by)}
            />
          </li>
        ))}
      </ol>
      <form onSubmit={submit} className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-3" noValidate>
        <h3 className="text-[14px] font-semibold text-ink">Add a column</h3>
        {create.error ? <InlineError error={create.error} /> : null}
        <Field
          label="New column"
          hint="For example: Waiting for a partner."
          size="compact"
          value={name}
          maxLength={SUPPORT_LIMITS.statusName}
          onChange={(event) => setName(event.target.value)}
          error={nameError}
          dir="auto"
          autoComplete="off"
        />
        <KindSelect label="Kind of the new column" value={kind} onChange={setKind} />
        <ColorPicker label="Colour of the new column" value={color} onChange={setColor} />
        <div>
          <Button type="submit" size="sm" loading={create.isPending} disabled={full}>
            Add column
          </Button>
          {full ? <p className="mt-1 text-[12px] text-ink-2">The board has {SUPPORT_LIMITS.statuses} columns. Archive one first.</p> : null}
        </div>
      </form>
    </Card>
  );
}

function ColumnRow({
  status,
  first,
  last,
  moving,
  onShift,
}: {
  status: AdminSupportStatus;
  first: boolean;
  last: boolean;
  moving: boolean;
  onShift: (by: -1 | 1) => void;
}) {
  const update = useUpdateSupportStatus();
  const [name, setName] = useDraft(status.name);
  const [archiving, setArchiving] = useState(false);
  const renamed = name.trim() !== "" && name.trim() !== status.name;

  function rename(event: FormEvent) {
    event.preventDefault();
    if (!renamed) return;
    update.mutate({ statusId: status.id, name: name.trim() }, { onSuccess: () => showToast("Column renamed."), onError });
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="flex items-center gap-2">
        <ColorDot color={status.color} size={12} />
        <span className="text-[15px] font-semibold text-ink" dir="auto">
          {status.name}
        </span>
        {status.isDefault ? <span className="rounded-pill bg-sunken px-2 py-0.5 text-[12px] font-semibold text-ink-2">Default for {KIND_WORDS[status.kind]}</span> : null}
      </p>
      <form onSubmit={rename} className="flex items-end gap-2" noValidate>
        <div className="min-w-0 flex-1">
          <Field
            label={`Name of ${status.name}`}
            size="compact"
            value={name}
            maxLength={SUPPORT_LIMITS.statusName}
            onChange={(event) => setName(event.target.value)}
            dir="auto"
            autoComplete="off"
          />
        </div>
        {renamed ? (
          <Button type="submit" size="sm" loading={update.isPending}>
            Save
          </Button>
        ) : null}
      </form>
      <KindSelect
        label={`Kind of ${status.name}`}
        value={status.kind}
        disabled={update.isPending || status.isDefault}
        onChange={(kind) => update.mutate({ statusId: status.id, kind }, { onSuccess: () => showToast("Kind changed."), onError })}
      />
      {status.isDefault ? <p className="text-[12px] text-ink-2">To change the kind or archive this column, make another column the default first.</p> : null}
      <ColorPicker
        label={`Colour of ${status.name}`}
        value={status.color}
        disabled={update.isPending}
        onChange={(color) => update.mutate({ statusId: status.id, color }, { onError })}
      />
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="secondary" disabled={first || moving} onClick={() => onShift(-1)}>
          Move left
        </Button>
        <Button type="button" size="sm" variant="secondary" disabled={last || moving} onClick={() => onShift(1)}>
          Move right
        </Button>
        {status.isDefault ? null : (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({ statusId: status.id, isDefault: true }, { onSuccess: () => showToast(`"${status.name}" is the default now.`), onError })
            }
          >
            Make default
          </Button>
        )}
        {status.isDefault ? null : (
          <Button type="button" size="sm" variant="ghost" onClick={() => setArchiving(true)}>
            Archive
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={archiving}
        title={`Archive the column "${status.name}"?`}
        confirmLabel="Archive"
        tone="danger"
        loading={update.isPending}
        onCancel={() => setArchiving(false)}
        onConfirm={() =>
          update.mutate(
            { statusId: status.id, archived: true },
            {
              onSuccess: () => {
                setArchiving(false);
                showToast("Column archived.");
              },
              onError: (error) => {
                setArchiving(false);
                onError(error);
              },
            },
          )
        }
      >
        The column leaves the board. A column with tickets cannot be archived: move its tickets first. You cannot undo this.
      </ConfirmDialog>
    </div>
  );
}

// --- saved replies ---------------------------------------------------------------------------

function SavedRepliesCard() {
  const saved = useSavedReplies();
  const create = useCreateSavedReply();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tried, setTried] = useState(false);
  const titleError = tried && !title.trim() ? "Write a title." : undefined;
  const bodyError = tried && !body.trim() ? "Write the text of the reply." : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    if (!title.trim() || !body.trim()) return;
    create.mutate(
      { title: title.trim(), body: body.trim() },
      {
        onSuccess: (reply) => {
          setTitle("");
          setBody("");
          setTried(false);
          showToast(`Saved reply "${reply.title}" added.`);
        },
      },
    );
  }

  return (
    <Card className="flex flex-col gap-4 p-4">
      <div>
        <h2 className="text-[16px] font-semibold text-ink">Saved replies</h2>
        <p className="text-[13px] text-ink-2">
          Text the team puts in a reply from the ticket page, then changes before it sends. Write {savedReplyFields.join(" or ")} and the
          ticket fills it in: the first name of the sender, or the name of the business.
        </p>
      </div>
      {saved.error && !saved.data ? <ErrorState error={saved.error} onRetry={() => void saved.refetch()} /> : null}
      {saved.data && saved.data.length === 0 ? <p className="text-[14px] text-ink-2">No saved replies yet.</p> : null}
      {saved.data && saved.data.length > 0 ? (
        <ul className="flex flex-col divide-y divide-line" aria-label="Saved replies">
          {saved.data.map((reply) => (
            <li key={reply.id} className="py-3">
              <SavedReplyRow reply={reply} />
            </li>
          ))}
        </ul>
      ) : null}
      <form onSubmit={submit} className="flex flex-col gap-3 rounded-xl border border-line bg-surface p-3" noValidate>
        <h3 className="text-[14px] font-semibold text-ink">Add a saved reply</h3>
        {create.error ? <InlineError error={create.error} /> : null}
        <Field
          label="Title"
          hint="Only the team sees it. For example: Reset the password."
          size="compact"
          value={title}
          maxLength={SUPPORT_LIMITS.savedReplyTitle}
          onChange={(event) => setTitle(event.target.value)}
          error={titleError}
          dir="auto"
          autoComplete="off"
        />
        <TextAreaField
          label="Text"
          rows={4}
          value={body}
          maxLength={SUPPORT_LIMITS.body}
          onChange={(event) => setBody(event.target.value)}
          error={bodyError}
          dir="auto"
        />
        <div>
          <Button type="submit" size="sm" loading={create.isPending}>
            Add saved reply
          </Button>
        </div>
      </form>
    </Card>
  );
}

function SavedReplyRow({ reply }: { reply: AdminSupportSavedReply }) {
  const update = useUpdateSavedReply();
  const remove = useDeleteSavedReply();
  const [title, setTitle] = useDraft(reply.title);
  const [body, setBody] = useDraft(reply.body);
  const [deleting, setDeleting] = useState(false);
  const changed = (title.trim() !== reply.title || body.trim() !== reply.body) && title.trim() !== "" && body.trim() !== "";

  function save(event: FormEvent) {
    event.preventDefault();
    if (!changed) return;
    update.mutate(
      {
        replyId: reply.id,
        ...(title.trim() !== reply.title ? { title: title.trim() } : {}),
        ...(body.trim() !== reply.body ? { body: body.trim() } : {}),
      },
      { onSuccess: () => showToast("Saved reply changed."), onError },
    );
  }

  return (
    <form onSubmit={save} className="flex flex-col gap-2" noValidate>
      <Field
        label="Title"
        size="compact"
        value={title}
        maxLength={SUPPORT_LIMITS.savedReplyTitle}
        onChange={(event) => setTitle(event.target.value)}
        dir="auto"
        autoComplete="off"
      />
      <TextAreaField label={`Text of ${reply.title}`} rows={3} value={body} maxLength={SUPPORT_LIMITS.body} onChange={(event) => setBody(event.target.value)} dir="auto" />
      <div className="flex flex-wrap gap-2">
        {changed ? (
          <Button type="submit" size="sm" loading={update.isPending}>
            Save
          </Button>
        ) : null}
        <Button type="button" size="sm" variant="ghost" onClick={() => setDeleting(true)}>
          Delete
        </Button>
      </div>
      <ConfirmDialog
        open={deleting}
        title={`Delete the saved reply "${reply.title}"?`}
        confirmLabel="Delete"
        tone="danger"
        loading={remove.isPending}
        onCancel={() => setDeleting(false)}
        onConfirm={() =>
          remove.mutate(reply.id, {
            onSuccess: () => {
              setDeleting(false);
              showToast("Saved reply deleted.");
            },
            onError,
          })
        }
      >
        The replies already sent with it stay in their tickets. You cannot undo this.
      </ConfirmDialog>
    </form>
  );
}
